import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import { Card } from "@/components/ui/card";
import { formatCurrencyMinor, type CurrencyCode } from "@/lib/currency/currency";
import { createQuoteLanguage, type QuoteLanguageMode } from "@/lib/export/quote-language";
import { formatTaxRatePercent } from "@/lib/pricing/tax-rate";

type QuoteSummaryProps = {
  subtotalMinor?: number | null;
  packagePriceEnabled?: number | boolean | null;
  packagePriceMinor?: number | null;
  taxAmountMinor?: number | null;
  taxRate?: number | null;
  grandTotalMinor?: number | null;
  currencyCode?: CurrencyCode | string | null;
  exchangeRate?: number | null;
  exchangeRateBaseCurrency?: CurrencyCode | string | null;
  languageMode?: QuoteLanguageMode;
};

export function QuoteSummary({
  subtotalMinor = 0,
  packagePriceEnabled = false,
  packagePriceMinor = 0,
  taxAmountMinor = 0,
  taxRate = 0,
  grandTotalMinor = 0,
  currencyCode = "RMB",
  exchangeRate = 1,
  exchangeRateBaseCurrency = "RMB",
  languageMode,
}: QuoteSummaryProps) {
  const { t } = useAppSettings();
  const quoteText = languageMode ? createQuoteLanguage(languageMode) : null;
  const summaryRows = [
    { label: quoteText?.pair("PRODUCTION COST", "明细合计") ?? t("summary.subtotal"), value: formatCurrencyMinor(subtotalMinor ?? 0, currencyCode) },
    { label: quoteText?.pair("Tax", "税金") ?? t("summary.tax"), rightLabel: formatTaxRatePercent(taxRate), value: formatCurrencyMinor(taxAmountMinor ?? 0, currencyCode) },
    { label: quoteText?.pair("GRAND TOTAL", "总计") ?? t("summary.grandTotal"), value: formatCurrencyMinor(grandTotalMinor ?? 0, currencyCode) },
    { label: quoteText?.pair("In Package", "打包价") ?? t("summary.package"), value: packagePriceEnabled ? formatCurrencyMinor(packagePriceMinor ?? 0, currencyCode) : "-" },
  ];
  const summaryTitle = quoteText?.pair("Summary", "汇总") ?? t("summary.title");
  const summaryNote = quoteText?.pair(
    "Validity, tax mode, watermark, and terms are loaded from the quote settings.",
    "有效期、税务模式、水印和条款将从报价设置中加载。",
  ) ?? t("summary.note");
  const hasReferenceRate = currencyCode && exchangeRateBaseCurrency && currencyCode !== exchangeRateBaseCurrency && Number(exchangeRate) > 0;

  return (
    <Card className="p-4">
      <h2 className="text-sm font-semibold">{summaryTitle}</h2>
      <div className="mt-3 space-y-2">
        {summaryRows.map(({ label, rightLabel, value }) => (
          <div key={label} className="flex items-center justify-between gap-4 text-sm">
            <span className="flex min-w-0 flex-1 items-center justify-between gap-3 text-muted-foreground">
              <span>{label}</span>
              {rightLabel ? <span className="shrink-0 text-right">{rightLabel}</span> : null}
            </span>
            <span className="shrink-0 font-medium">{value}</span>
          </div>
        ))}
      </div>
      <div className="mt-5 border-t border-border pt-4 text-xs text-muted-foreground">
        {summaryNote}
        {hasReferenceRate ? (
          <span className="mt-2 block">
            1 {currencyCode} = {Number(exchangeRate).toFixed(4)} {exchangeRateBaseCurrency}
          </span>
        ) : null}
      </div>
    </Card>
  );
}
